import Link from "next/link";
import { useRouter } from "next/router";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import classNames from "classnames";
import { twMerge } from "tailwind-merge";

import { faAngleDoubleLeft } from "@fortawesome/free-solid-svg-icons/faAngleDoubleLeft";
import { faAngleDoubleRight } from "@fortawesome/free-solid-svg-icons/faAngleDoubleRight";

import FontAwesomeIcon from "components/FontAwesomeIcon";
import useActivityTracker from "hooks/useActivityTracker";
import useAuthentication from "hooks/useAuthentication";
import useMediaQuery from "hooks/useMediaQuery";
import useWindowSize from "hooks/useWindowSize";

import styles from "./style.module.scss";

type ProfilePageTabsProps = {
  slug: string;
  profile: any;
};

const ProfilePageTabs = ({ slug, profile }: ProfilePageTabsProps) => {
  const router = useRouter();
  const { mobile } = useMediaQuery();
  const { trackEvent } = useActivityTracker();

  const { authenticated } = useAuthentication();

  const isCompany = router.route.startsWith("/companies/[slug]");
  const containerRef = useRef<HTMLDivElement>(null);

  const {
    showAnalysisTab,
    showDiscussionsTab,
    showTeamTab,
    showPremiumServicesTab,
  } = profile || {};

  useEffect(() => {
    if (router.route === "/profiles/[slug]/research" && !showAnalysisTab) {
      router.replace(`/profiles/${slug}/about`);
    }
    if (router.route === "/companies/[slug]/research" && !showAnalysisTab) {
      router.replace(`/companies/${slug}/about`);
    }
    if (
      router.route === "/profiles/[slug]/discussions" &&
      !showDiscussionsTab
    ) {
      router.replace(`/profiles/${slug}/about`);
    }
    if (
      router.route === "/companies/[slug]/discussions" &&
      !showDiscussionsTab
    ) {
      router.replace(`/companies/${slug}/about`);
    }
    if (router.route === "/companies/[slug]/team" && !showTeamTab) {
      router.replace(`/companies/${slug}/about`);
    }
  }, [router, showAnalysisTab, showDiscussionsTab, showTeamTab, slug]);

  const tabs = useMemo(
    () => [
      ...(showAnalysisTab
        ? [
            {
              name: "Research",
              href: `/${isCompany ? "companies" : "profiles"}/${slug}/research`,
            },
          ]
        : []),
      ...(showDiscussionsTab
        ? [
            {
              name: "Discussions",
              noFollow: true,
              href: `/${
                isCompany ? "companies" : "profiles"
              }/${slug}/discussions`,
            },
          ]
        : []),
      {
        name: "About",
        noFollow: true,
        href: `/${isCompany ? "companies" : "profiles"}/${slug}/about`,
      },
      ...(showTeamTab
        ? [
            {
              name: "Team",
              href: `/${isCompany ? "companies" : "profiles"}/${slug}/team`,
            },
          ]
        : []),
      ...(authenticated && showPremiumServicesTab
        ? [
            {
              name: "Premium Subscriptions",
              href: `/${
                isCompany ? "companies" : "profiles"
              }/${slug}/premium-subscriptions`,
            },
          ]
        : []),
    ],
    [
      authenticated,
      isCompany,
      showAnalysisTab,
      showDiscussionsTab,
      showPremiumServicesTab,
      showTeamTab,
      slug,
    ]
  );

  const scrollableRef = useRef<HTMLDivElement>(null);
  const { width } = useWindowSize();
  const [showLeftNav, setShowLeftNav] = useState(false);
  const [showRightNav, setShowRightNav] = useState(false);
  const scroll = useCallback(
    (direction) => {
      const scrollable = scrollableRef.current;
      const scrollLeft = scrollable?.scrollLeft || 0;
      const offset = (direction === "right" ? 1 : -1) * Math.round(width / 2);
      scrollable?.scroll({ left: scrollLeft + offset, behavior: "smooth" });
    },
    [width]
  );

  const scrollToTab = useCallback((tab) => {
    const scrollable = scrollableRef.current;

    const scrollLeft =
      tab.offsetLeft -
      (scrollable?.offsetLeft || 0) +
      tab.offsetWidth -
      (scrollable?.scrollWidth || 0) / 2;

    scrollable?.scroll({
      left: scrollLeft,
      behavior: "smooth",
    });
  }, []);

  const onTabClick = useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>, tab: string) => {
      try {
        const tab = event.currentTarget;
        scrollToTab(tab);
      } catch (error) {
        console.debug(error);
      }

      try {
        trackEvent("Tab - Click", {
          event_category: "Tab",
          event_action: "Click",
          event_label: tab,
        });
      } catch (error) {
        console.debug(error);
      }
    },
    [scrollToTab, trackEvent]
  );

  const activeTab = useRef<HTMLSpanElement | null>(null);
  const onTabLoad = useCallback(
    (ref, tab) => {
      if (
        !!ref &&
        (router.asPath === tab.href || router.asPath.startsWith(`${tab.href}?`))
      ) {
        activeTab.current = ref;
      }
    },
    [router.asPath]
  );
  useEffect(() => {
    if (activeTab.current) scrollToTab(activeTab.current);
  }, [scrollToTab]);

  useEffect(() => {
    const scrollable = scrollableRef.current;

    const updateNavButtons = () => {
      if (scrollable?.scrollLeft === 0) {
        setShowLeftNav(false);
      } else {
        setShowLeftNav(true);
      }
      if (
        (scrollable?.scrollLeft || 0) + width >=
        //TODO Find descripancy pixel (0.5px) on scrollwidth
        (scrollable?.scrollWidth || 0) - 1
      ) {
        setShowRightNav(false);
      } else {
        setShowRightNav(true);
      }
    };

    updateNavButtons();

    scrollable?.addEventListener("scroll", updateNavButtons, { passive: true });

    return () => {
      scrollable?.removeEventListener("scroll", updateNavButtons);
    };
  }, [width]);

  const [stickyTop, setStickyTop] = useState(false);
  const [top, setTop] = useState(0);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  useEffect(() => {
    if (!mobile) return;

    const container = containerRef.current;
    const upperNavBar = 0;

    const updateTabsPosition = () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => {
        const top =
          container?.getBoundingClientRect()?.top || Number.MAX_SAFE_INTEGER;
        setStickyTop(top < upperNavBar);
        setTop(top < 0 ? 0 : top);
      }, 1);
    };

    updateTabsPosition();

    window.addEventListener("scroll", updateTabsPosition, { passive: true });

    return () => {
      window.removeEventListener("scroll", updateTabsPosition);
    };
  }, [mobile]);

  useEffect(() => {
    if (typeof window === "undefined") return;

    if (mobile) {
      const container = containerRef.current;
      const top = container?.getBoundingClientRect()?.top || 0;
      if (top < 0) {
        window.scrollTo({
          top: 460,
        });
      }
    }
  }, [mobile, router.asPath]);

  return (
    <>
      <div
        className={classNames(
          styles.placeholder,
          mobile && styles.mobileLayout,
          stickyTop && styles.stickyTabs
        )}
        ref={containerRef}
      />
      <div
        className={classNames(
          styles.entityPageTabs,
          mobile && styles.mobileLayout,
          stickyTop && styles.stickyTabs,
          "mx-[-5px] rounded-none bg-white shadow-md mb-[-15px] z-10",
          "md:mx-0 md:rounded-xl md:bg-transparent md:shadow-none md:mb-0",
          "skiptranslate"
        )}
        style={stickyTop ? { top } : {}}
      >
        {showLeftNav && showPremiumServicesTab && (
          <button
            title="scroll left"
            className={classNames(styles.tabsNav, styles.left)}
            onClick={() => scroll("left")}
          >
            <FontAwesomeIcon icon={faAngleDoubleLeft} />
          </button>
        )}
        <div className={classNames(styles.tabs)} ref={scrollableRef}>
          {tabs.map((tab: any) => {
            return (
              <Link
                href={tab.href}
                key={tab.name}
                rel={tab.noFollow && "nofollow"}
                prefetch={false}
                scroll={!mobile}
                onClick={(e) => onTabClick(e, tab.name)}
                className={twMerge(
                  router.asPath === tab.href ||
                    router.asPath.startsWith(`${tab.href}?`)
                    ? styles.activeTab
                    : undefined,
                  showPremiumServicesTab && "whitespace-nowrap"
                )}
                ref={(ref) => onTabLoad(ref, tab)}
              >
                {tab.name}
              </Link>
            );
          })}
        </div>
        {showRightNav && showPremiumServicesTab && (
          <button
            title="scroll right"
            className={classNames(styles.tabsNav, styles.right)}
            onClick={() => scroll("right")}
          >
            <FontAwesomeIcon icon={faAngleDoubleRight} />
          </button>
        )}
      </div>
      {stickyTop && <div className="h-[48px] w-full" />}
    </>
  );
};

export default ProfilePageTabs;
